import * as yup from "yup";

import {
  FieldRequestFieldEnum,
  FieldRequestTypeEnum,
} from "@/features/common/enums";
import type { IFieldRequestInput } from "@/features/lender-responses/types/lender-response";

export const createBidFieldRequestSchema = (
  totalLoanAmount?: number,
): yup.ObjectSchema<IFieldRequestInput> =>
  yup.object().shape({
    field: yup
      .mixed<FieldRequestFieldEnum>()
      .oneOf(Object.values(FieldRequestFieldEnum))
      .required(),
    lenderComment: yup.string().nullable(),
    isCollectedByLender: yup.boolean().default(false),
    type: yup
      .mixed<FieldRequestTypeEnum>()
      .oneOf(Object.values(FieldRequestTypeEnum))
      .required(),
    config: yup
      .object()
      .nullable()
      .shape({
        numberOfRequiredGuarantors: yup
          .number()
          .required("Number of guarantors is required")
          .min(1, (schema) => `Can't be fewer than ${schema.min}`)
          .max(10, (schema) => `Can't be more than ${schema.max}`),
        reducedGuarantorPercentTo: yup
          .number()
          .nullable()
          .min(1, (schema) => `Can't be less than ${schema.min}`)
          .max(99, (schema) => `Can't be more than ${schema.max}`)
          .test(
            "mutually-exclusive-with-commitment-amount",
            "Cannot set both percentage and commitment amount",
            function (value) {
              const { guarantorCommitmentAmount } = this.parent;
              if (value != null && guarantorCommitmentAmount != null) {
                return false;
              }
              return true;
            },
          ),
        guarantorCommitmentAmount: yup
          .number()
          .nullable()
          .min(0, (schema) => `Can't be less than ${schema.min}`)
          .test(
            "mutually-exclusive-with-percent",
            "Cannot set both commitment amount and percentage",
            function (value) {
              const { reducedGuarantorPercentTo } = this.parent;
              if (value != null && reducedGuarantorPercentTo != null) {
                return false;
              }
              return true;
            },
          )
          .test(
            "max-offer-amount",
            "Cannot exceed the total offer amount",
            function (value) {
              if (value == null) return true;
              if (totalLoanAmount != null) {
                return totalLoanAmount === 0 || value <= totalLoanAmount;
              }
              const root = this.from?.[this.from.length - 1]?.value as
                | { bids?: Array<{ amount?: number; purchaseLimit?: number }> }
                | undefined;
              if (!root?.bids?.length) return true;
              const totalAmount = root.bids.reduce(
                (sum, bid) => sum + (bid.amount ?? bid.purchaseLimit ?? 0),
                0,
              );
              if (totalAmount === 0) return true;
              return value <= totalAmount;
            },
          ),
      })
      .when("checked", {
        is: true,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable(),
      }),
  });
